import { useState, useEffect } from 'react';
import {
  type DeploymentTargets,
  FRONTEND_OPTIONS,
  API_OPTIONS,
  DATABASE_OPTIONS,
  DEFAULT_TARGETS,
  getDefaultTargets,
  formatTargetsSummary,
} from '../../config/stack-deploy-map';

interface Props {
  stack: string;
  value: DeploymentTargets;
  onChange: (value: DeploymentTargets) => void;
}

/**
 * Multi-layer deployment target selector.
 * Auto-populates Frontend / API / Database from the selected tech stack.
 * Each layer can be independently overridden by the user.
 * Supabase is always the default database.
 */
export default function DeploymentTargetField({ stack, value, onChange }: Props) {
  const [overrides, setOverrides] = useState<Partial<Record<keyof DeploymentTargets, boolean>>>({});
  const [customValues, setCustomValues] = useState<Partial<Record<keyof DeploymentTargets, string>>>({});
  const [showResetPrompt, setShowResetPrompt] = useState(false);
  const [pendingDefaults, setPendingDefaults] = useState<DeploymentTargets | null>(null);

  // Auto-fill from stack when stack changes
  useEffect(() => {
    if (!stack) return;
    const defaults = getDefaultTargets(stack) ?? DEFAULT_TARGETS;
    const hasOverrides = Object.values(overrides).some(Boolean);

    if (!hasOverrides) {
      // No manual overrides — auto-fill all layers
      onChange(defaults);
    } else {
      // User has overrides — show reset prompt if defaults differ
      const differs = (
        (value.frontend !== defaults.frontend && !overrides.frontend) ||
        (value.api !== defaults.api && !overrides.api) ||
        (value.database !== defaults.database && !overrides.database)
      );
      if (differs) {
        setPendingDefaults(defaults);
        setShowResetPrompt(true);
      }
    }
  }, [stack]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLayerChange = (layer: keyof DeploymentTargets, newValue: string) => {
    const defaults = getDefaultTargets(stack) ?? DEFAULT_TARGETS;
    const isDefault = defaults[layer] === newValue;

    setOverrides(prev => ({ ...prev, [layer]: !isDefault }));

    if (newValue === 'custom') {
      setCustomValues(prev => ({ ...prev, [layer]: '' }));
      // Don't update the value yet — wait for custom text input
    } else {
      setCustomValues(prev => ({ ...prev, [layer]: undefined }));
      onChange({ ...value, [layer]: newValue });
    }
  };

  const handleCustomInput = (layer: keyof DeploymentTargets, text: string) => {
    setCustomValues(prev => ({ ...prev, [layer]: text }));
    onChange({ ...value, [layer]: text || 'custom' });
  };

  const handleResetAll = () => {
    if (pendingDefaults) {
      onChange(pendingDefaults);
      setOverrides({});
      setCustomValues({});
    }
    setShowResetPrompt(false);
  };

  const handleKeepAll = () => {
    setShowResetPrompt(false);
  };

  const isDisabled = !stack;

  const renderLayer = (
    layer: keyof DeploymentTargets,
    label: string,
    options: Array<{ label: string; value: string }>,
  ) => {
    const currentValue = value[layer];
    const isCustom = currentValue === 'custom' || (currentValue && !options.some(o => o.value === currentValue));
    const defaults = getDefaultTargets(stack) ?? DEFAULT_TARGETS;
    const isAutoDetected = !overrides[layer] && currentValue === defaults[layer];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 160 }}>
        <label style={{
          fontSize: 11,
          color: 'var(--text-muted)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontWeight: 500,
        }}>
          {label}
          {isAutoDetected && !isDisabled && (
            <span style={{
              fontSize: 9,
              color: 'var(--text-dim)',
              background: 'var(--surface-3)',
              padding: '1px 5px',
              borderRadius: 3,
              fontWeight: 400,
            }}>
              auto
            </span>
          )}
        </label>
        <select
          value={options.some(o => o.value === currentValue) ? currentValue : (isCustom ? 'custom' : '')}
          onChange={e => handleLayerChange(layer, e.target.value)}
          disabled={isDisabled}
          style={{
            padding: '6px 10px',
            background: isDisabled ? 'var(--surface-1)' : 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 5,
            color: isDisabled ? 'var(--text-dim)' : 'var(--text)',
            fontSize: 12,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.6 : 1,
          }}
        >
          <option value="" disabled>Select…</option>
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {isCustom && (
          <input
            value={customValues[layer] ?? (currentValue !== 'custom' ? currentValue : '')}
            onChange={e => handleCustomInput(layer, e.target.value)}
            placeholder="e.g. Fly.io"
            style={{
              padding: '5px 8px',
              fontSize: 11,
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: 4,
              color: 'var(--text)',
            }}
            autoFocus
          />
        )}
      </div>
    );
  };

  return (
    <div className="wizard-field">
      <label style={{ marginBottom: 4, display: 'block' }}>
        Deployment Targets
      </label>

      {!isDisabled && (
        <div style={{
          fontSize: 11,
          color: 'var(--text-dim)',
          marginBottom: 8,
        }}>
          {formatTargetsSummary(value)}
        </div>
      )}

      <div style={{
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
      }}>
        {renderLayer('frontend', 'Frontend', FRONTEND_OPTIONS)}
        {renderLayer('api', 'API / Backend', API_OPTIONS)}
        {renderLayer('database', 'Database', DATABASE_OPTIONS)}
      </div>

      {/* Reset confirmation prompt */}
      {showResetPrompt && (
        <div style={{
          marginTop: 8,
          padding: '8px 12px',
          background: 'rgba(255,160,0,0.08)',
          border: '1px solid rgba(255,160,0,0.3)',
          borderRadius: 6,
          fontSize: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}>
          <span style={{ color: 'var(--text)', flex: 1 }}>
            Tech stack changed — reset deployment targets to defaults?
          </span>
          <button
            onClick={handleResetAll}
            style={{
              padding: '3px 10px',
              fontSize: 11,
              background: 'var(--orange)',
              color: '#000',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Reset
          </button>
          <button
            onClick={handleKeepAll}
            style={{
              padding: '3px 10px',
              fontSize: 11,
              background: 'var(--surface-3)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Keep mine
          </button>
        </div>
      )}
    </div>
  );
}
